/**
 * Timestamps are stored UTC and rendered in the workspace timezone.
 *
 * That distinction matters more than it looks. A restaurant crew works across
 * midnight; if a cook in a different timezone (or a phone that never got its
 * tz right) sees "11:45 PM" as "8:45 PM", the shift-handover notes are wrong
 * in a way nobody notices until it costs someone a delivery.
 *
 * So every formatter here takes the workspace zone explicitly. There is no
 * "use the device's timezone" path on purpose.
 */

const cache = new Map<string, Intl.DateTimeFormat>();

function formatter(zone: string, options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  const key = `${zone}:${JSON.stringify(options)}`;
  let existing = cache.get(key);
  if (!existing) {
    // Constructing a DateTimeFormat is surprisingly expensive, and a chat log
    // formats one per message on every render.
    existing = new Intl.DateTimeFormat('en-US', { ...options, timeZone: zone });
    cache.set(key, existing);
  }
  return existing;
}

export function timeOfDay(ts: number, zone: string): string {
  return formatter(zone, { hour: 'numeric', minute: '2-digit' }).format(ts);
}

export function dayLabel(ts: number, zone: string): string {
  const today = dayKey(Date.now(), zone);
  const day = dayKey(ts, zone);
  if (day === today) return 'Today';

  const yesterday = dayKey(Date.now() - 86_400_000, zone);
  if (day === yesterday) return 'Yesterday';

  return formatter(zone, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    ...(new Date(ts).getFullYear() === new Date().getFullYear() ? {} : { year: 'numeric' }),
  }).format(ts);
}

/** Stable YYYY-MM-DD *in the workspace zone*, for grouping messages by day. */
export function dayKey(ts: number, zone: string): string {
  return formatter(zone, { year: 'numeric', month: '2-digit', day: '2-digit' }).format(ts);
}

/** "now", "4m", "2h", "Tue" — for channel list rows, where space is tight. */
export function shortAgo(ts: number, zone: string): string {
  const diff = Date.now() - ts;
  if (diff < 60_000) return 'now';
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h`;
  if (diff < 7 * 86_400_000) return formatter(zone, { weekday: 'short' }).format(ts);
  return formatter(zone, { month: 'numeric', day: 'numeric' }).format(ts);
}

export function fullTimestamp(ts: number, zone: string): string {
  return formatter(zone, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(ts);
}
